/**
 * ── DISPLAY VARIANT CARDS: the same metric, drawn as a chart or a map ───────────────
 *
 * A card is a **(metric x display x dimension)** triple. `roiCardDefinitions` and
 * `OVERVIEW_STAT_BOXES` supply the metric half; `analyticsDisplay` decides which displays
 * each one may take; this file turns every ALLOWED pair into a real `AnalyticsCardItem`
 * the library can offer and the canvas can place.
 *
 * ## The set is small on purpose, and its smallness is the honest answer
 *
 * Vera stores current STATE, not events (`VRG-362`), so almost nothing on this page has a
 * history to plot. Run against today's card set, the loop below produces FOUR variants
 * out of seventeen metrics x two displays — the two dimensions the statistics endpoint
 * buckets and the scans-map endpoint locates. Every other pair comes back incapable with
 * a reason, and no variant is generated for it. That is the feature working: an
 * affordance offered where the data cannot honour it is the defect this epic has already
 * shipped three times.
 *
 * ## Why a variant PINS its dimension
 *
 * The legacy `scans_by_time` / `scans_by_location` panels carry an
 * `AnalyticsDimensionToggle` in their header and read `filters.chartDataType` /
 * `filters.mapDataType` from redux — one card, a dimension the reader flips. A variant is
 * the opposite: it exists BECAUSE the reader picked a metric and asked for it as a chart,
 * so its dimension is its identity. It therefore passes `dataType` explicitly and states
 * the dimension in its TITLE instead of offering a toggle for it.
 *
 * That also keeps the two independent. Flipping the legacy chart to Certificates must not
 * silently re-point a card the reader created as "Total Scans over time" — which is the
 * same class of bug that split the single global `dataType` in two (`AnalyticsFilters`).
 *
 * ## THE OVERLAP IS RESOLVED: the legacy panels ARE these cards now (VRG-481)
 *
 * `scans_by_time` and `scans_by_location` no longer exist. They were the same
 * measurements these variants draw, reached through a Scans/Certificates toggle in their
 * headers instead of through the metric you picked — so the library carried THREE rows
 * for one thing (`Total Scans`, `Total Scans over time`, `Scans by time`).
 *
 * The dimension toggle is not lost, it is re-expressed: what it used to switch between is
 * now `Total Scans -> Chart` and `Total Certificates -> Chart`, two cards you can hold at
 * once rather than one card with a hidden mode. Their ids forward-map on read — see
 * `migrateAnalyticsCardId`, which reads the stored `chartDataType` so a view that was
 * showing certificates keeps showing certificates.
 *
 * ## What the variants INHERITED from those panels, and what they did not
 *
 * The header controls that are INDEPENDENT of the dimension came across, because dropping
 * them would have been a silent capability regression riding along with a naming change:
 *
 *   chart   the certificate-status LEGEND and the line/bar MARK toggle
 *   map     the all/latest MODE toggle (scans only — certificates pins `latest` internally)
 *
 * What did NOT come across is the `AnalyticsDimensionToggle`, and that is the point: a
 * variant's dimension IS its identity, so a control that repointed it would make the
 * card's title a lie. It used to render an `AnalyticsDimensionChip` in that slot instead
 * — the same information, stated rather than offered — but VERA-260 removed that too:
 * the title already names the dimension ("Total Scans" / "Total Certificates"), so a chip
 * repeating "Scans" / "Certificates" beside it stated the same noun a third time (the
 * title, the description, and the chip). `AnalyticsDimensionChip` has no remaining call
 * sites and was deleted rather than left as a dead export.
 *
 * The map's mode is ONE page-level value shared by every map card, exactly as it was when
 * there was one map card. Keying it per card id is real work for a control that only
 * applies to the scans dimension, and nothing yet asks for two maps in different modes.
 */
import React from 'react'

import { Box, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import { XIcon } from 'lucide-react'

import {
  ANALYTICS_MAP_CARD_SIZE,
  ANALYTICS_MAP_GROUPING_CAVEAT,
  ANALYTICS_VALUE_RECONSTRUCTION_NOTE,
  AnalyticsMapGroupingToggle,
  analyticsCardDimension,
  analyticsCardRankKey,
  analyticsCardValueDimension,
  analyticsComposedDisplaySupported,
  analyticsCountryGroupingValue,
  analyticsDisplayCapability,
  analyticsMapGroupingCapability,
  analyticsMapMetricLabel,
  analyticsValueLabel,
  analyticsValueReconstruction,
  analyticsVariantCardId,
  parseAnalyticsVariantCardId,
  type AnalyticsCardCategory,
  type AnalyticsCardItem,
  type AnalyticsMapGrouping,
} from 'components/analytics'
import type { TopInfluencerDimension } from '@repo/types'
import type {
  AnalyticsDataType,
  AnalyticsFilters,
  AnalyticsSeriesDimension,
} from 'store/analytics/types'

import {
  ScansByCountry,
  ScansByLocation,
  ScansByLocationLegend,
  ScansByLocationModeToggle,
  ScansByTime2,
  ScansByTimeLegend,
  ScansByTimeMarkToggle,
  TopInfluencers,
  TOP_INFLUENCER_DIMENSION_LABELS,
  composedSeriesColor,
  scansByLocationDescription,
  scansByTimeDescription,
  topInfluencersDescription,
  type ScansMapMode,
} from './components'

// This card's "View by" bucket width — a per-card display control, rendered in the header
// beside the mark toggle (VRG-512). Direct file import, not the barrel: the analytics
// barrel re-exports `analyticsExport`, which this module has no reason to pull in.
import { AnalyticsCardGranularityControl } from 'components/analytics/AnalyticsCardGranularityControl'

/** What a variant needs from the page to render. */
interface DisplayVariantContext {
  filters: AnalyticsFilters
  /**
   * The map's all/latest mode, lifted to the page.
   *
   * It has to come from outside: the toggle renders into the card shell's header ACTION
   * slot, which is a sibling of `render` rather than inside it, so the two halves meet
   * only at the page.
   */
  scansMapMode: ScansMapMode
  onScansMapModeChange: (next: ScansMapMode) => void
  /**
   * The leaderboard's grouping axis (VERA-260), lifted for exactly the reason above —
   * its toggle lives in the header `action` slot, its consumer in `render`.
   *
   * PAGE-LEVEL rather than keyed per card, which is the same call the map's mode makes
   * and rests on the same fact: only one metric is table-capable today (`scans_count`),
   * so at most one leaderboard card can exist and there is no second card to disagree
   * with. Keying per card id is real work for a control with one consumer. If a second
   * dimension ever becomes rankable, this and `scansMapMode` should both be keyed — they
   * will have acquired the same problem at the same time.
   */
  topInfluencerDimension: TopInfluencerDimension
  onTopInfluencerDimensionChange: (next: TopInfluencerDimension) => void
  /**
   * The map's `Group by:` handler (VERA-260) — `view.setCardGrouping`.
   *
   * ⚠️ NOT a piece of page state, unlike the two above, and the asymmetry is the point.
   * Grouping is not held anywhere: it IS the card's id (`…::map@country`), so switching it
   * RENAMES the card in place rather than writing to a store. That is what makes it
   * survive a reload and a saved view for free — see the `@` separator's own note in
   * `analyticsGrid` for why the alternative (a per-card preference store) would have had
   * to be persisted and migrated separately.
   *
   * So the page hands down a rename, and this file hands it a card id and a grouping.
   */
  onCardGroupingChange: (cardId: string, grouping: AnalyticsMapGrouping) => void
}

/**
 * The groupings a map can take, in the order the toggle offers them.
 *
 * Typed as the union itself, so a grouping added to `AnalyticsMapGrouping` without being
 * listed here is a compile error rather than an id set that silently omits it — the same
 * discipline `ANALYTICS_VARIANT_DISPLAYS` applies to the display axis.
 */
const ANALYTICS_MAP_GROUPINGS: readonly AnalyticsMapGrouping[] = ['pin', 'country'] as const

/** The metric cards a variant can be built from — id and display name, nothing more. */
export interface DisplayVariantSource {
  id: string
  title: string
}

/**
 * ── A VARIANT IS TITLED FOR ITS METRIC, WITH NO DISPLAY SUFFIX (VRG-481) ────────────
 *
 * It was `${metricTitle} over time` / `${metricTitle} by location`. Both suffixes are
 * gone, and the reason they existed at all is the reason they can go: they were there to
 * tell SIBLING LIBRARY ROWS apart, back when `Total Scans` and `Total Scans over time`
 * were two entries in one scanned list. The library now carries one row per metric, so
 * there is no sibling to disambiguate — and the suffix was restating the card's TYPE
 * inside its NAME, which the card already says three other ways.
 *
 * ⚠️ THE COLLISION THIS USED TO GUARD AGAINST NO LONGER EXISTS, and it is worth saying
 * why rather than leaving the old warning to look violated. The previous note forbade
 * shortening these because "Certificates by location" was exactly the legacy map card's
 * own title in certificates mode — but `scans_by_location` is gone (see the file header),
 * so there is nothing left to collide with.
 *
 * What CAN now share a title is a metric's TILE and its chart, both "Total Scans", on one
 * canvas. That is accepted rather than overlooked: they are a 3x1 stat tile and a
 * full-width plot carrying a chart glyph, so nothing about them reads as duplicated — and
 * it is the direct consequence of the model the user asked for, where a display is a
 * property of a metric rather than a different thing with a different name.
 */
const variantTitle = (metricTitle: string): string => metricTitle

/* ────────────────────────────────────────────────────────────────────────────────────
   VRG-467 — COMPOSED CARDS: a chart holding MORE THAN ONE metric's series
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * The per-series remove control for a composed card's header (VRG-467's "reverse
 * gesture" — a composed card needs a way to shed a series or a user could compose
 * themselves into an arrangement they cannot undo without the whole-card `X`).
 *
 * Lives HERE rather than as a chip inside `ScansByTimeLegend` because the two legends
 * answer different questions and must not be visually merged into one control that
 * appears to do both: `ScansByTimeLegend` toggles a certificate-status series' VISIBILITY
 * (reversible, local, never changes what is on the canvas); this REMOVES a metric from
 * the card's identity (rewrites the card's id — see `useAnalyticsView.removeCardSeries`).
 * Collapsing them into one legend would make a click's consequence depend on which chip
 * you happened to hit.
 *
 * Colours read `composedSeriesColor(theme, index)` — the SAME pure function
 * `ScansByTime2` draws the line from, so the swatch can never drift from the mark it
 * names (the same argument `ScansByTimeLegend`'s own header makes for `seriesColors`).
 *
 * The primary metric (`metricIds[0]`) is NOT offered here — it has no "remove series"
 * affordance because removing it would leave the OTHER metric as the card's sole series
 * with the primary's title still showing, which is a worse UX than just saying "remove
 * the primary via the whole-card X, or remove the others first." Only the metrics that
 * were COMPOSED ONTO the card (index ≥ 1) get a remove control.
 */
const ComposedSeriesLegend = ({
  sources,
  onRemove,
}: {
  sources: readonly DisplayVariantSource[]
  onRemove: (metricId: string) => void
}) => {
  const theme = useTheme()
  // index 0 is the primary metric — see the header above for why it has no remove chip.
  const composed = sources.slice(1)
  if (composed.length === 0) return null

  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap' }}>
      {composed.map((source, i) => {
        const color = composedSeriesColor(theme, i)
        return (
          <Tooltip key={source.id} title={`Remove ${source.title} from this chart`}>
            <Box
              component="button"
              type="button"
              onClick={() => onRemove(source.id)}
              aria-label={`Remove ${source.title} from this chart`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                px: 2,
                py: 0.5,
                m: 0,
                font: 'inherit',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1,
                minHeight: 24,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: alpha(color, 0.4),
                backgroundColor: alpha(color, 0.1),
                color: 'text.secondary',
                cursor: 'pointer',
                '&:hover': { backgroundColor: alpha(color, 0.18) },
              }}
            >
              {/* The swatch, not a status glyph — this row names a MEASUREMENT, not a
                  display type, so it borrows the dot-plus-label shape `ScansByTimeLegend`
                  uses rather than `AnalyticsCategoryGlyph` (which answers "chart vs map",
                  not "which metric"). */}
              <Box
                component="span"
                sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }}
              />
              <Typography component="span" variant="inherit">
                {source.title}
              </Typography>
              <XIcon size={12} strokeWidth={1.5} />
            </Box>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

/**
 * Build the card for ONE COMPOSED variant id — a card the user created by dragging a
 * metric onto an existing chart (`AnalyticsCanvas.onComposeDrop` ->
 * `useAnalyticsView.composeCard`), rather than one of the enumerated single-metric pairs
 * `buildAnalyticsDisplayVariantCards` produces up front.
 *
 * A composed id cannot be enumerated in advance — it is created by the user, not derived
 * from a fixed metric list — so `AnalyticsPage` calls this once per composed id currently
 * present in `view.cardIds`, alongside the enumerated variants. `null` is a valid,
 * expected answer (not an error): a stale composed id whose metrics no longer resolve
 * (the ROI catalogue changed under it) degrades to "render nothing" via
 * `AnalyticsCanvas`'s existing `cardById.get(id)` miss path — the SAME graceful-hole
 * behaviour every other unknown id on this page already gets, rather than a special case
 * here.
 *
 * ⚠️ CHART ONLY, AND THE RULE IS NO LONGER RESTATED HERE (VERA-260). This used to carry
 * its own `parsed.display !== 'chart'` under its own paragraph, and
 * `AnalyticsCanvas.isComposeDropTarget` carried a second copy under a second paragraph —
 * two expressions of one rule, which is how they came to describe different worlds while
 * returning the same boolean. Both said a map was excluded because `ScansByLocation` takes
 * a single `dataType`; by the time anyone read them `MAP_RENDERABLE` had gained a third
 * mappable dimension and the choropleth had shipped beside it, so the stated premise was
 * gone and the conclusion happened to survive.
 *
 * The rule now lives once, in `analyticsComposedDisplaySupported`. The canvas asks for the
 * SENTENCE (`analyticsComposeTargetRefusal`, which must explain itself to a reader
 * mid-drag); this asks for the BOOLEAN, because a builder has nobody to explain to — an id
 * it refuses degrades to `AnalyticsCanvas`'s existing `cardById.get(id)` miss path, the
 * same graceful hole every other unresolvable id gets.
 *
 * Why a map still cannot compose at all: its only free channel is the mark's COLOUR and
 * colour is already the measure — a pin is coloured by scan outcome, a country by its own
 * value. See `analyticsComposeTargetRefusal` and `AnalyticsMapGrouping`.
 */
export const buildComposedDisplayVariantCard = (
  cardId: string,
  sourceById: ReadonlyMap<string, DisplayVariantSource>,
  {
    filters,
    onRemoveSeries,
  }: DisplayVariantContext & {
    /** `view.removeCardSeries` — the per-series remove control's handler. */
    onRemoveSeries: (targetCardId: string, metricId: string) => void
  },
): AnalyticsCardItem | null => {
  const parsed = parseAnalyticsVariantCardId(cardId)
  if (!parsed || parsed.metricIds.length < 2) return null
  // ONE expression of the rule — see the header note above.
  if (!analyticsComposedDisplaySupported(parsed.display)) return null

  const sources = parsed.metricIds
    .map(id => sourceById.get(id))
    .filter((s): s is DisplayVariantSource => Boolean(s))
  // Every metric id has to resolve, or the composed title/series list would silently
  // drop the one that didn't — degrade to no card at all rather than a card that lies
  // about which metrics it holds.
  if (sources.length !== parsed.metricIds.length) return null

  const primary = sources[0]
  const primaryDimension = analyticsCardDimension(primary.id)
  if (!primaryDimension || !analyticsDisplayCapability(primary.id).chart.capable) return null
  const dataType = primaryDimension as AnalyticsSeriesDimension

  const composedSeries = sources.slice(1).flatMap(source => {
    const dimension = analyticsCardDimension(source.id)
    // Belt and braces: `AnalyticsCanvas.isComposeDropTarget` already gates the drop on
    // capability, so a series that fails this check should be unreachable — dropped
    // rather than thrown, matching the graceful-hole discipline this whole function
    // follows for a stale id.
    if (!dimension) return []
    return [
      { metricId: source.id, dataType: dimension as AnalyticsSeriesDimension, label: source.title },
    ]
  })

  const composedTitle = sources.map(s => s.title).join(' + ')

  return {
    id: cardId,
    kind: 'panel',
    title: composedTitle,
    description: `${sources.map(s => s.title).join(' and ')}, plotted together over the selected period.`,
    category: 'chart',
    summary: `${composedTitle}, plotted together.`,
    /*
      ── THE CROWDED HEADER, AND THE ORDER IS A RULE RATHER THAN AN ARRANGEMENT ────────

      This slot holds the most variable content on the page: a status legend (up to six
      chips), one remove-chip PER COMPOSED SERIES (unbounded — one per drop), and now two
      fixed controls. So the two are separated explicitly:

        [ legends — variable, `minWidth: 0`, yields ] [ granularity ] [ mark ]  ← fixed

      The legend group is what shrinks. `ComposedSeriesLegend` already wraps its own chips,
      so yielding costs it height rather than content, while the two controls are
      `flexShrink: 0` and keep their full width at any series count. Without the
      `minWidth: 0` a flex item refuses to shrink past its content, and an eighth series
      would push the controls out of the card instead.

      Granularity sits LEFT of the mark toggle, per the ordering the simple chart card uses
      — variable content first, then the card's own display controls, coarsest-effect last.
    */
    action: (
      <Stack direction="row" alignItems="center" spacing={3} sx={{ minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={3}
          sx={{ minWidth: 0, justifyContent: 'flex-end' }}
        >
          <ScansByTimeLegend statuses={filters.certificateStatuses} dimension={dataType} />
          <ComposedSeriesLegend
            sources={sources}
            onRemove={metricId => onRemoveSeries(cardId, metricId)}
          />
        </Stack>
        <AnalyticsCardGranularityControl
          cardId={cardId}
          from={filters.dateRange.from}
          to={filters.dateRange.to}
        />
        <ScansByTimeMarkToggle cardId={cardId} />
      </Stack>
    ),
    render: ({ fillHeight }) => (
      <ScansByTime2
        cardId={cardId}
        period="30"
        globalCertificateStatuses={filters.certificateStatuses}
        dateFrom={filters.dateRange.from}
        dateTo={filters.dateRange.to}
        dataType={dataType}
        composedSeries={composedSeries}
        ownerIds={filters.ownerIds}
        productIds={filters.productIds}
        certificateIds={filters.certificateIds}
        location={filters.location}
        expanded={fillHeight}
      />
    ),
  }
}

/**
 * Every display variant the current card set can honestly offer.
 *
 * Order: all charts, then all maps, following `ANALYTICS_CARD_CATEGORIES`' authored order
 * — the same order the library's type filter sorts by, so filtering reads as a narrowing
 * rather than a reshuffle.
 */
export const buildAnalyticsDisplayVariantCards = (
  sources: readonly DisplayVariantSource[],
  {
    filters,
    scansMapMode,
    onScansMapModeChange,
    topInfluencerDimension,
    onTopInfluencerDimensionChange,
    onCardGroupingChange,
  }: DisplayVariantContext,
): AnalyticsCardItem[] => {
  const charts: AnalyticsCardItem[] = []
  const maps: AnalyticsCardItem[] = []
  const tables: AnalyticsCardItem[] = []

  /**
   * ── ONE MAP CARD BUILDER, TWO GROUPINGS ─────────────────────────────────────────────
   *
   * Called once per (metric, grouping) pair the metric can honestly take, so a
   * both-capable metric like `scans` produces TWO ids — `scans_count::map` and
   * `scans_count::map@country` — and a country-only one (an invalid-scan RATE) produces
   * just the second.
   *
   * ⚠️ BOTH ARE BUILT EVEN THOUGH ONLY ONE IS EVER ON THE CANVAS AT A TIME, and that is
   * required rather than wasteful. This array is what the canvas renders FROM, keyed by
   * id: the `Group by:` toggle works by RENAMING the card, so the id it renames TO must
   * already have a definition here or the moment a reader flips the toggle their card
   * becomes a hole. It is also what `availableCardIds` is derived from, so an id absent
   * here is one `intersectOrdered` drops out of every saved view holding it — which is
   * exactly what would happen to anyone who saved a country-grouped map.
   *
   * The two share EVERYTHING that is a property of the metric (title, size, summary) and
   * differ only in the body they render and the sentence describing it, which is the whole
   * claim grouping makes: one measurement, two granularities.
   */
  const pushMapCard = (
    source: DisplayVariantSource,
    grouping: AnalyticsMapGrouping,
    /** Only the pin body needs it, and only counts have one. */
    dataType: AnalyticsSeriesDimension | null,
  ) => {
    const cardId = analyticsVariantCardId(source.id, 'map', grouping)
    const countryValue = analyticsCountryGroupingValue(source.id)
    const pinCapable = analyticsMapGroupingCapability(source.id, 'pin').capable
    const isCertificates = dataType === 'certificates'

    maps.push({
      id: cardId,
      kind: 'panel',
      /*
        ⚠️ THE COUNTRY GROUPING TAKES A SUFFIX, and it is the one place `variantTitle`'s
        "no display suffix" rule does not apply (VERA-260).

        That rule exists because a suffix was disambiguating SIBLING LIBRARY ROWS that no
        longer sit side by side — one row per metric, so there is nothing to tell apart.
        The country map puts two rows back: a metric row `Scans` and a PRESET row
        (`analyticsIsLibraryPresetCardId`) which, titled by the same rule, was ALSO `Scans`.
        Two identically-named rows one above the other is precisely the confusion that rule
        was written to end, so following it literally here would have violated it.

        Caught by looking at the rendered library, not by reading the code.

        It also reads correctly on the canvas: flipping `Group by:` renames the card, which
        is honest — the card genuinely became a different card (its id changed), and the
        header says so rather than leaving the title asserting pins over a choropleth.
      */
      title: grouping === 'country' ? `${source.title} by country` : variantTitle(source.title),
      /*
        ⚠️ THE GROUPING CAVEAT IS NOT REPEATED HERE, and it was on the first pass — the
        card carried it in this description AND in the choropleth's own footnote, so the
        same 40-word sentence appeared twice within one card, ~500px apart. Caught by
        reading the rendered card rather than the code.

        The footnote is the right one of the two: it sits AT the number the caveat is
        about, under the legend that assigns the colours. This line is the card's
        one-sentence identity — it is also what the library shows — and a caveat is not
        an identity. The axis itself is explained by the `Group by:` tooltip, which reads
        the same constant, so the sentence still has exactly one author.
      */
      description:
        grouping === 'country'
          ? `${analyticsMapMetricLabel(source.id)}, per country.`
          : scansByLocationDescription(dataType ?? 'scans'),
      category: 'map',
      /*
        NOUN-PHRASED, not a sentence with a verb — `${label} varies by country` was the
        first draft and it disagrees for half the metrics ("How scans varies by country").
        The labels are a mix of singular statistics ("Invalid scan rate") and plural counts
        ("Invalid scans"), so no single conjugation is correct for all of them and picking
        one per metric would be a second label table.
      */
      summary:
        grouping === 'country'
          ? `${analyticsMapMetricLabel(source.id)} per country, as a filled world map.`
          : `Where ${analyticsMapMetricLabel(source.id).toLowerCase()} happened, as a map beside the individual records.`,
      // The SAME size spec at both groupings, from the one constant — two renders of one
      // card that sized differently would be the drift a shared default exists to stop.
      size: ANALYTICS_MAP_CARD_SIZE,
      /**
       * ── THE HEADER: `Group by:` ALWAYS, then the pin body's own controls ───────────
       *
       * The grouping toggle is present at BOTH groupings and is never hidden, because it
       * is the control that gets you back — §46's obligation on the pin-only controls
       * beside it, which DO disappear at `country` grouping (along with the whole
       * `ScansByLocation` body they belong to).
       *
       * ⚠️ THE `Pins | Heat` RENDER TOGGLE IS DELIBERATELY NOT HERE. It stays on the map
       * canvas, inside `ScansByLocation`, and `AnalyticsMapGroupingToggle`'s own docblock
       * carries the full argument: it is a SUB-choice of the pin grouping (heat is a
       * render of the same located events), so seating it beside its own parent — with
       * both controls showing an option labelled `Pins`, 16px apart, meaning two different
       * things — would read as a duplicate rather than as a hierarchy. Parent in the
       * chrome, child on the surface it modifies.
       */
      action: (
        <Stack direction="row" alignItems="center" spacing={4}>
          <AnalyticsMapGroupingToggle
            metricCardId={source.id}
            value={grouping}
            onChange={next => onCardGroupingChange(cardId, next)}
          />
          {grouping === 'pin' && (
            <>
              <ScansByLocationLegend />
              {!isCertificates && (
                <ScansByLocationModeToggle value={scansMapMode} onChange={onScansMapModeChange} />
              )}
            </>
          )}
        </Stack>
      ),
      render: ({ fillHeight }) =>
        grouping === 'country' && countryValue ? (
          <ScansByCountry
            filters={filters}
            value={countryValue}
            label={analyticsMapMetricLabel(source.id)}
            /*
              The caveat rides the FOOTNOTE only when the metric also has a pin grouping —
              i.e. only when there is a pin count for the reader to compare this fill
              against. On a country-only rate the sentence would name a comparison that
              cannot be made, so it is left to the `Group by:` tooltip, which explains the
              axis rather than this number.
            */
            footnoteSuffix={pinCapable ? ANALYTICS_MAP_GROUPING_CAVEAT : undefined}
            // Maximised, the card's own box decides the height; gridded, it takes the same
            // 560 the pin map does.
            height={fillHeight ? '100%' : 560}
          />
        ) : (
          <ScansByLocation
            // `latest` is forced internally for certificates; passing the shared mode is
            // still correct because that card ignores it in that branch.
            mode={scansMapMode}
            dataType={dataType ?? undefined}
            expanded={fillHeight}
          />
        ),
    })
  }

  for (const source of sources) {
    const capability = analyticsDisplayCapability(source.id)
    const dimension = analyticsCardDimension(source.id)

    /*
      ── THE TABLE VARIANT (VERA-260) — the page's DISTRIBUTION cards ────────────────

      TWO cards today, one per rankable metric: Total Scans (`scans`, via
      `TABLE_RANKABLE`) and Leads Captured (`leads`, via `ROI_CARD_RANK_KEY`). It was one
      until the ranking key became a parameter — the leaderboard has always COMPUTED both
      columns and could only ever ORDER by scans, so "the owners who brought the most new
      scanners" was unanswerable while the data for it sat in the response.

      That is still the honest smallness the file header describes: an affordance is
      generated where the data can honour it and nowhere else. Two is simply how many
      rankings the endpoint can currently order by.

      Unlike the chart and the map, the table's title carries a SUFFIX. Those two dropped
      theirs because one row per metric left no sibling to disambiguate — but this card's
      subject is not the metric, it is the metric's BREAKDOWN, and "Total Scans" alone
      would name a figure while showing a ranking. The suffix names the axis and moves
      with the toggle, so the title always says what the rows are.
    */
    if (capability.table.capable) {
      /*
        WHICH COLUMN THE ROWS ARE ORDERED AND TRUNCATED ON — read from the registry, not
        assumed. It reaches the request (so the server ranks correctly) AND the card's
        copy (so the description says which ranking it is). Those two must come from one
        value or a card can describe a ranking it did not ask for.
      */
      const rankBy = analyticsCardRankKey(source.id)
      tables.push({
        id: analyticsVariantCardId(source.id, 'table'),
        kind: 'panel',
        title: `${source.title} by ${TOP_INFLUENCER_DIMENSION_LABELS[
          topInfluencerDimension
        ].toLowerCase()}`,
        description: topInfluencersDescription(topInfluencerDimension, rankBy),
        category: 'table',
        summary: `${source.title}, ranked by owner, product or place.`,
        /*
          ── TWO THIRDS WIDTH, FULL HEIGHT (VERA-260) ────────────────────────────────

          `w: 8` of `ANALYTICS_GRID_COLS.lg`'s 12 — two thirds of the row — where every
          other panel defaults to the full 12. The height is UNCHANGED at 5 rows, and the
          two are decided separately because they answer different questions.

          WIDTH is decided by the content's own measured need. The five columns are one
          FLEXIBLE name column (220px `minWidth`) plus four fixed numeric columns sized for
          their headers (§49): 220 + 110 + 110 + 150 + 140 = 730px, and the grid reports
          `scrollWidth` 731. So 731px of INNER grid width is the threshold below which the
          name column hits its floor and the grid scrolls horizontally.

          Measured in the browser (canvas card width, minus 52px of card chrome, is the
          grid's inner width):

          | viewport | full-width panel | `w: 6` card | `w: 8` card | inner at 8 | vs 731 |
          |---|---|---|---|---|---|
          | 1440 | 1311 | ~643 | 866 | 814 | **83px of slack** |
          | 1280 | 1151 | ~563 | 759 | 707 | **24px short** |

          ⚠️ SO `w: 6` WAS BADLY WRONG AND `w: 8` IS NOT PERFECT. At 6 the inner width is
          ~511px against 730px of columns — the name column pinned at its floor and roughly
          220px of horizontal scroll, at BOTH viewports. 8 clears it outright at 1440 (the
          name column gets 814 − 511 = 303px, a real remainder) and leaves **24px** at 1280,
          which is a ~97% reduction rather than a fix.

          The old note claimed 6 was "the measured half-width pane `panel.minW` is itself
          set from". True of `minW` — and `minW` is a FLOOR, not a fit; reading it as a fit
          is how the default came to sit below the content's own requirement.

          The residual 24px is NOT closed by widening further as a matter of principle: the
          arithmetic says `w: 9` would clear 1280 (card ~857, inner ~805), but the honest
          fix is the COLUMN sizing, not the card — four fixed columns totalling 510px for
          four numbers is generous, and §49 governs that. Left at 8 deliberately, and
          recorded here so the next person does not re-derive it. Horizontal scroll at 1280
          is a known pre-existing condition on several tables (§49 says so); the headers
          themselves stay whole at every width above, which is what §49 actually forbids.

          It stays PAIRABLE in the way that matters, which the full-width default forbids:
          a ranking is read AGAINST something — the chart it breaks down, or a second
          leaderboard on another axis. 8 + 4 does not seat two of these side by side, but
          the packer puts a 4-wide tile column beside it, and a reader who wants two
          rankings can still drag both to 6.

          ⚠️ `panel.minW` is 6, so this can now be dragged NARROWER than its default — down
          to the arrangement that was previously forced. That is the correct relationship
          (a default above its floor), and it is the change that makes the floor mean what
          it says.

          HEIGHT stays at 5 rows for the reason it was set: the content is a LIST, and its
          usefulness is a function of how many rows clear the fold. Five rows (~896px)
          shows the full default ten plus the header and the unattributed note; four cut
          the ranking off around row six, which is exactly where a leaderboard stops being
          one. Widening the card does not change how many ROWS fit, so nothing about the
          width decision above bears on it.

          Existing saved views are NOT re-laid-out by this — `resolveLayout` takes `w`/`h`
          from the stored item and only re-applies the CONSTRAINTS from this spec, so a
          reader who already placed this card keeps their arrangement. The new default
          reaches anyone who has not, plus every newly added one.
        */
        size: { w: 8, h: 5, minH: 2 },
        /*
          ⚠️ NO `action`. The Group-by toggle renders INSIDE the card body, above the
          table — see `TopInfluencers` for the measurement that forced it there. In the
          header's action slot it collapsed the title/description box to 5px at this
          card's half-width default, because `CardSectionHeader` never shrinks the action.

          This is why `TopInfluencers` now takes `onDimensionChange`: the control and its
          consumer are finally in the same subtree. The dimension itself stays PAGE-level
          (see `DisplayVariantContext`) because the card's TITLE reads from it too.
        */
        render: () => (
          <TopInfluencers
            dimension={topInfluencerDimension}
            onDimensionChange={onTopInfluencerDimensionChange}
            rankBy={rankBy}
            filters={filters}
          />
        ),
      })
    }
    /*
      ⚠️ THE DIMENSION GUARD SITS BELOW THE TABLE, NOT ABOVE IT (VERA-260).

      It used to be the first thing in the loop, which silently made a series dimension a
      precondition for EVERY display. That is true of the chart and the map — both plot
      per-event rows and have nothing to draw without one — and false of the table, whose
      rows come from the leaderboard endpoint rather than from the statistics series.
      `mkt_leads_captured` is the case that exposed it: a distinct count, correctly absent
      from `ROI_CARD_DIMENSION`, and correctly rankable by owner. The `continue` skipped it
      before its own capability flag was ever consulted.

      So the table block above is reached on the capability alone, and this guard now
      protects only the two displays that genuinely need a dimension. See
      `ROI_CARD_RANK_KEY` in `analyticsDisplay` for the two-axis argument.
    */
    /*
      ── THE VALUE CHART (VERA-260) — a rate/mean/median card's own series ────────────

      Reached BEFORE the dimension guard below, for the same reason the table block is:
      these cards have no COUNT dimension and would otherwise be skipped by a `continue`
      before their capability flag was ever consulted — the exact defect the note above
      records for `mkt_leads_captured`, one axis over.

      `valueDimension` and `dimension` are mutually exclusive by construction (the two
      unions are asserted disjoint in `store/analytics/types`), so this never competes with
      the count branch below for the same card.

      NO LEGEND, and that is not an omission. `ScansByTimeLegend` toggles the
      CERTIFICATE-STATUS breakdown, and a rate has none — "the invalid scan rate of Active
      certificates" is not this measurement, it is a differently-filtered one. The mark
      toggle stays: line-vs-bar is a legitimate choice for a value series too.
    */
    const valueDimension = analyticsCardValueDimension(source.id)

    /*
      ── THE VALUE MAP (VERA-260) — a rate drawn as a choropleth ──────────────────────

      Reached BEFORE the dimension guard, like the value chart below it and for the same
      reason: these cards have no COUNT dimension and would be skipped by the `continue`
      before their capability was ever consulted.

      ⚠️ THIS IS THE ENTRY THE GROUPING AXIS EXISTS FOR. A rate has no event to pin — a pin
      is present or absent, so 4% and 96% draw the identical picture — but colouring an
      AREA by a rate is the canonical use of a choropleth, and the country endpoint has
      been able to compute both of these since it shipped. Under the old model that
      capability was reachable only through a standalone card with an in-card picker; under
      this one it is just the map, grouped by country. `COUNTRY_GROUPABLE` decides which
      value dimensions qualify (two of six), and the other four keep their own refusal
      sentences.

      PIN GROUPING IS NOT OFFERED HERE and is not omitted by oversight: for these cards it
      is incapable by construction, so `pushMapCard` is called for `country` only and the
      toggle renders `Pins` disabled with the pin refusal as its tooltip.
    */
    if (!dimension && analyticsMapGroupingCapability(source.id, 'country').capable) {
      pushMapCard(source, 'country', null)
    }

    if (!dimension && valueDimension && capability.chart.capable) {
      const chartCardId = analyticsVariantCardId(source.id, 'chart')
      charts.push({
        id: chartCardId,
        kind: 'panel',
        title: variantTitle(source.title),
        // The reconstruction sentence IS the description. A value chart's single most
        // important caption is how it relates to the figure on the card beside it, and
        // the classification that decides it is total over the value union — so a new
        // statistic cannot reach this surface without one.
        description: `${analyticsValueLabel(valueDimension)} over the selected period. ${
          ANALYTICS_VALUE_RECONSTRUCTION_NOTE[analyticsValueReconstruction(valueDimension)]
        }`,
        category: 'chart',
        summary: `${source.title}, plotted over the selected period.`,
        // Granularity then mark — this card's two display controls, in the same order the
        // dimension chart below uses. A VALUE chart buckets exactly like a count chart
        // does, so it gets the same control rather than an argument about why it does not.
        action: (
          <Stack direction="row" alignItems="center" spacing={3}>
            <AnalyticsCardGranularityControl
              cardId={chartCardId}
              from={filters.dateRange.from}
              to={filters.dateRange.to}
            />
            <ScansByTimeMarkToggle cardId={chartCardId} />
          </Stack>
        ),
        render: ({ fillHeight }) => (
          <ScansByTime2
            cardId={chartCardId}
            period="30"
            globalCertificateStatuses={filters.certificateStatuses}
            dateFrom={filters.dateRange.from}
            dateTo={filters.dateRange.to}
            valueDimension={valueDimension}
            ownerIds={filters.ownerIds}
            productIds={filters.productIds}
            certificateIds={filters.certificateIds}
            location={filters.location}
            expanded={fillHeight}
          />
        ),
      })
    }
    if (!dimension) continue
    /*
      ONE ASSERTION SINCE VERA-260, where there were two. The chart and the map both take
      the WIDE union now, and the leaderboard — the one consumer that took the narrow
      `AnalyticsDataType` — no longer takes a dimension from here at all (its rows are
      grouped by `topInfluencerDimension`, a different axis entirely). The second
      assertion, `narrowDataType`, was already dead before it was removed: declared,
      described in two comments below, and read by nothing.

      It remains an assertion rather than a check because the guard is the capability call
      above: `analyticsDisplayCapability` reports `chart.capable` and `map.capable` only
      for a member of this union (see `isSeriesDimension` there), and each is read strictly
      inside the branch its own flag opened.
    */
    const dataType = dimension as AnalyticsSeriesDimension

    if (capability.chart.capable) {
      const chartCardId = analyticsVariantCardId(source.id, 'chart')
      charts.push({
        id: chartCardId,
        kind: 'panel',
        title: variantTitle(source.title),
        description: scansByTimeDescription(dataType),
        category: 'chart',
        summary: `${source.title}, plotted over the selected period.`,
        /**
         * The legacy chart panel's header, minus two controls that could not survive.
         *
         * Legend · mark toggle — `AnalyticsDimensionToggle` is gone (replaced by nothing:
         * the dimension is this card's identity, see the file header) and so, as of
         * VERA-260, is the DIMENSION CHIP that used to sit rightmost of it: the card's
         * title already names the dimension ("Total Scans" / "Total Certificates"), so a
         * chip repeating "Scans" / "Certificates" beside it said the same noun a third
         * time. No divider either — a chip was the only thing it separated the mark
         * toggle FROM.
         *
         * The legend's 420px cap and its `flex-end` box come across unchanged:
         * `CardSectionHeader` puts this slot in a `flexShrink: 0` box, so an uncapped
         * six-chip legend pushes the card title instead of wrapping.
         *
         * `ScansByTimeMarkToggle` and the paired `ScansByTime2` below both take
         * `chartCardId` — the SAME id this card is registered under — so this card's
         * line/bar mark can never leak into a sibling chart card (VERA-260).
         */
        action: (
          <Stack direction="row" alignItems="center" spacing={3}>
            <Box sx={{ maxWidth: 420, display: 'flex', justifyContent: 'flex-end' }}>
              <ScansByTimeLegend statuses={filters.certificateStatuses} dimension={dataType} />
            </Box>
            {/* THIS CARD's bucket, not the page's (VRG-512) — same `chartCardId` as the
                mark toggle and the chart below, so all three read one card's state. It
                sits left of the mark toggle: both are display controls, and the legend
                (variable width) stays leftmost so the fixed pair never moves as statuses
                are selected. */}
            <AnalyticsCardGranularityControl
              cardId={chartCardId}
              from={filters.dateRange.from}
              to={filters.dateRange.to}
            />
            <ScansByTimeMarkToggle cardId={chartCardId} />
          </Stack>
        ),
        render: ({ fillHeight }) => (
          <ScansByTime2
            cardId={chartCardId}
            period="30"
            globalCertificateStatuses={filters.certificateStatuses}
            dateFrom={filters.dateRange.from}
            dateTo={filters.dateRange.to}
            // PINNED. Not `filters.chartDataType` — see the file header.
            dataType={dataType}
            ownerIds={filters.ownerIds}
            productIds={filters.productIds}
            certificateIds={filters.certificateIds}
            location={filters.location}
            expanded={fillHeight}
          />
        ),
      })
    }

    /*
      ── THE MAP VARIANTS, ONE PER CAPABLE GROUPING (VERA-260) ────────────────────────

      This used to be a single `if (capability.map.capable)` producing one card. It is now
      per-grouping, and the two are NOT redundant: `capability.map` is the UNION over
      groupings (see `analyticsMapCapability`), so testing it here would have built a pin
      map for a metric only a country fill can draw.

      ⚠️ `dataType` — THE WIDE UNION — since VERA-260, where this read a narrow
      `AnalyticsDataType` assertion (since removed; see the loop head). The map's props
      were typed to `AnalyticsDataType` because its endpoint's `mode` names two values, and
      that conflated "the request has a `mode`" with "the request can only ask two
      questions". `invalid_scans` is asked by a pinned `scan_statuses` instead, so the
      narrow type was excluding a dimension the endpoint had been able to serve all along.
      See `MAP_RENDERABLE`.

      The per-grouping capability calls remain exactly the guard that makes the assertion
      sound — each is total over this same wide union.

      A both-capable count (`scans`, `invalid_scans`) produces two cards here and the
      reader holds ONE of them, flipping between the ids with the toggle. `certificates` is
      pin-only by a deliberate `false` — a country fill would count an item once per
      country it was scanned in while the pin map places it once at its latest location, so
      toggling would silently RAISE the total. That refusal reaches the reader as the
      disabled `Countries` option's tooltip.
    */
    if (analyticsMapGroupingCapability(source.id, 'pin').capable) {
      pushMapCard(source, 'pin', dataType)
    }
    if (analyticsMapGroupingCapability(source.id, 'country').capable) {
      pushMapCard(source, 'country', dataType)
    }
  }

  // Charts, then maps, then tables — `ANALYTICS_CARD_CATEGORIES`' authored order, which
  // the library's type filter also sorts by, so filtering reads as a narrowing.
  /*
    ── THE STANDALONE CHOROPLETH CARD IS GONE (VERA-260, render pass) ─────────────────

    `scans_by_country` used to be pushed here, once, outside the loop — the one card that
    was not a (metric x display) pair. Its own note called that "structural rather than a
    shortcut", on two arguments that the grouping axis has since answered:

      1. "Three metrics from ONE pre-aggregated response, so generating three cards would
         issue the same request three times." True, and it is now three DEFINITIONS rather
         than three cards on a canvas: a reader holds one at a time, and the ones they do
         not hold issue nothing. `useAnalyticsCountryMetrics` is a query hook keyed on the
         filters, so even two country cards on one canvas share a single request.
      2. "Two of its metrics have no metric card to be a variant OF." That was true of the
         PIN map's capability record, not of the page: `mkt_invalid_scan_rate` and
         `mkt_repeat_scan_rate` are perfectly real ROI cards. What they lacked was a
         capability record that could say a CHOROPLETH may draw them, which is exactly what
         `COUNTRY_GROUPABLE` now is.

    The picker inside it is likewise gone — its three columns map onto three
    `COUNTRY_GROUPABLE` entries exactly — and the axis gained a fourth measure the picker
    never offered (`invalid_scans` as a per-country COUNT). Its id forward-maps in
    `migrateAnalyticsCardId`, so every saved view holding it still opens.
  */
  return [...charts, ...maps, ...tables]
}

/**
 * The retired choropleth card's id, still re-exported.
 *
 * ⚠️ IT IS AN ALIAS NOW, NOT A CARD (VERA-260). Nothing builds a card under this id any
 * more — `migrateAnalyticsCardId` forward-maps it to `scans_count::map@country` on read.
 * The constant survives because the id is PERSISTED in saved views and because
 * `analyticsExport` still recognises it, and it lives in
 * `components/analytics/analyticsGrid` because that module owns every other card id; this
 * is a re-export so existing importers keep working.
 */
export { SCANS_BY_COUNTRY_CARD_ID } from 'components/analytics'


/**
 * Every variant id the page can render, as a MODULE-CONSTANT derivation.
 *
 * `AnalyticsPage`'s `availableCardIds` is memoised with `[]` deps on purpose — it feeds
 * `useAnalyticsView`, whose `applyState` identity `ViewsDropdown` holds, so it must not
 * change on every fetch. Capability is a function of TYPES, not of data, so the id set is
 * genuinely static and can be computed the same way.
 *
 * ⚠️ Titles are irrelevant here and are passed as the id: this returns ids only, and
 * `variantTitle` never runs. Keeping one function for "which pairs are capable" is what
 * stops this list and the cards above disagreeing about what exists.
 */
export const analyticsDisplayVariantIds = (metricCardIds: readonly string[]): string[] =>
  /*
    ⚠️ `SCANS_BY_COUNTRY_CARD_ID` IS DELIBERATELY NO LONGER LISTED (VERA-260). It used to
    lead this array because it was the one card with no metric behind it to derive from.
    It is not a card any more — `migrateAnalyticsCardId` rewrites it to
    `scans_count::map@country` BEFORE `intersectOrdered` ever compares against this list
    (see `migratedState`, which migrates then intersects, in that order), so listing it
    would advertise an id nothing renders.
  */
  metricCardIds.flatMap(id => {
    const capability = analyticsDisplayCapability(id)
    // Derived from `ANALYTICS_VARIANT_DISPLAYS` rather than three hand-written branches:
    // a display added to that tuple appears here with no edit, which is what stopped
    // `table` needing one. The previous shape was a literal per display and would have
    // gone stale silently — a variant the library offers and this list omits is a card
    // `intersectOrdered` quietly drops from every saved view.
    return ANALYTICS_VARIANT_DISPLAYS.filter(display => capability[display].capable).flatMap(
      display => {
        /*
          ⚠️ A MAP CONTRIBUTES ONE ID PER CAPABLE GROUPING, NOT ONE PER DISPLAY (VERA-260).

          `capability.map` is the union over groupings, so the old single
          `analyticsVariantCardId(id, display)` produced the PIN id for every map-capable
          metric — including the rates, which have no pin map at all, and excluding
          `scans_count::map@country`, which is the id the toggle renames TO and the id a
          reader's saved "by country" view holds. Both failures are the same one this
          function's own comment names: an id the page renders that this list omits is a
          card `intersectOrdered` silently drops.

          Enumerated from `analyticsMapGroupingCapability` rather than from the pair
          (`default`, `other`), so a third grouping would appear here by adding a member to
          `AnalyticsMapGrouping` and answering for it in `COUNTRY_GROUPABLE`.
        */
        if (display !== 'map') return [analyticsVariantCardId(id, display)]
        return ANALYTICS_MAP_GROUPINGS.filter(
          grouping => analyticsMapGroupingCapability(id, grouping).capable,
        ).map(grouping => analyticsVariantCardId(id, 'map', grouping))
      },
    )
  })

/**
 * The displays a metric can be drawn AS, in registry order. Metric is the base.
 *
 * ⚠️ Typed as the registry's own non-`metric` keys, so it cannot drift from
 * `ANALYTICS_CARD_CATEGORIES`: adding a category without listing it here is a compile
 * error, and listing one that does not exist is too. That is what makes the derivation
 * above safe.
 */
export const ANALYTICS_VARIANT_DISPLAYS: readonly Exclude<
  AnalyticsCardCategory,
  'metric'
>[] = ['chart', 'map', 'table'] as const
